import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Close } from '@liveauctioneers/hammer-ui-core/icons';
import { BodySecondary, H2, H4 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { QRCodeSVG } from 'qrcode.react';

import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { AppDownloadSource, type AppDownloadSourceValue } from '@/utils/urls/appDownloadRedirectUrl';

import { MOBILE_PHONE_SCREENSHOT_LEFT_URL } from '../constants';

const QR_STEP_KEYS = ['qrStep1', 'qrStep2', 'qrStep3'] as const;

export type DownloadMobilePromoDesktopProps = {
    className?: string;
    downloadUrl: string;
    onClose: () => void;
    source?: AppDownloadSourceValue;
};

export const DownloadMobilePromoDesktop = ({
    className,
    downloadUrl,
    onClose,
    source = AppDownloadSource.LeadingBidder,
}: DownloadMobilePromoDesktopProps) => {
    const formatAppMessage = useAppFormatMessage();
    const titleKey = source === AppDownloadSource.SavedItem ? 'titleSavedItem' : 'title';

    // Figma (Place-Bid / App prompt, Modal: Core 447:18179): 256 + 588 = 844px. Header + footer match Figma;
    // the QR + list body uses px-6 (not Figma’s px-64) so the instruction copy stays on one line at this width.
    return (
        <div
            className={cn(
                'mx-auto flex w-full max-w-[844px] flex-nowrap items-stretch overflow-x-auto overflow-y-hidden bg-background-primary shadow-02',
                'md:mx-0 md:max-w-none md:overflow-x-hidden',
                className
            )}
        >
            <div className="flex w-[256px] min-w-0 shrink-0 flex-col items-center justify-center overflow-hidden bg-background-secondary p-lg">
                <div className="relative aspect-[494/1017] min-h-0 w-full">
                    <div className="pointer-events-none absolute inset-0 overflow-hidden">
                        <img
                            alt=""
                            className="absolute left-[-7.89%] top-[-0.69%] h-[101.18%] w-[112.35%] max-w-none"
                            src={MOBILE_PHONE_SCREENSHOT_LEFT_URL}
                        />
                    </div>
                </div>
            </div>

            <div className="relative flex h-full min-h-0 w-[588px] shrink-0 flex-col overflow-hidden bg-background-primary pb-[138px]">
                <IconButton
                    aria-label={formatAppMessage('downloadMobilePromo', 'closeLabel')}
                    className="absolute right-sm top-sm z-10"
                    icon={Close}
                    iconStyle="bold"
                    onClick={() => {
                        onClose();
                    }}
                    size="md"
                    style="neutral"
                    variant="transparent"
                />
                <header className="flex w-full min-w-0 flex-col gap-1.5 p-sm">
                    <div
                        aria-hidden
                        className="h-[42px] w-full min-w-0 shrink-0"
                    />
                    <div className="flex w-full min-w-0 flex-col items-center gap-xs px-xl text-center">
                        <H2 className="hui-text-h2 text-center tracking-[0.2px]">
                            {formatAppMessage('downloadMobilePromo', titleKey)}
                        </H2>
                        <BodySecondary
                            className="text-center"
                            color="secondary"
                        >
                            {formatAppMessage('downloadMobilePromo', 'subtitleDesktop')}
                        </BodySecondary>
                    </div>
                </header>

                <div className="w-full min-w-0 px-6 py-sm">
                    <div className="w-full min-w-0 border border-border-secondary p-1">
                        <div className="flex w-full min-w-0 items-center gap-xs p-2xs">
                            <div
                                aria-label={formatAppMessage('downloadMobilePromo', 'qrCodeAlt')}
                                className="relative flex size-32 shrink-0 items-center justify-center p-2xs"
                                role="img"
                            >
                                <QRCodeSVG
                                    level="M"
                                    size={120}
                                    value={downloadUrl}
                                />
                            </div>
                            <ol className="min-w-0 flex-1 list-decimal space-y-0 pl-6 [list-style-position:outside]">
                                {QR_STEP_KEYS.map((stepKey) => (
                                    <li
                                        className="leading-6 tracking-[0.2px] marker:text-text-primary"
                                        key={stepKey}
                                    >
                                        <H4>{formatAppMessage('downloadMobilePromo', stepKey)}</H4>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
